import Button from '@components/Button';
import { HideFiltersTab } from '@components/HideFiltersTab';
import IconMdi from '@components/IconMdi';
import InputMultiSelect from '@components/InputMultiSelect';
import { COMPACT_MULTI_SELECT_STYLE_PROPS } from '@components/InputMultiSelect/constants';
import InputSelect from '@components/InputSelect';
import { Show, ShowFirstMatching } from '@components/Show';
import Toggle from '@components/Toggle';
import { mdiClose } from '@mdi/js';
import type { DropdownFilter } from '@pages/User/components/table/shared/types/table.types';
import { filtersOrderMap } from '@pages/User/helpers/filters/filtersOrderMap';
import { getColumnFilterOptions } from '@pages/User/helpers/filters/getColumnFilterOptions';
import type { Table } from '@tanstack/react-table';
import clsx from 'clsx';
import { cn } from 'cn';
import { get } from 'lodash-es';

const selectClasses = 'block rounded-md border-info-300 focus:border-primary-500 focus:ring-primary-500 sm:text-sm';

interface FiltersBarProps {
  entityName: string;
  table: Table<any>;
  wrapperClassName?: string;
  onClearAllFilters: () => void;
  onHide?: () => void;
}

export const FiltersBar = ({ entityName, table, wrapperClassName, onClearAllFilters, onHide }: FiltersBarProps) => {
  const tableColumns = table.getAllColumns();

  const hasActiveFilters = table.getState().columnFilters.some((filter) => {
    if (Array.isArray(filter.value)) {
      return filter.value.length > 0;
    }
    return filter.value;
  });

  return (
    <>
      <div data-testid="filters-bar" className="mt-4 border border-gray-200 bg-gray-50 p-4 pt-1 md:rounded-lg">
        <div className="flex flex-row items-start xl:flex">
          <div className={clsx('flex flex-wrap items-start gap-5', wrapperClassName)}>
            {tableColumns
              .filter((column) => column.getCanFilter())
              .sort((a, b) => {
                const aColumnId = get(a, 'columnDef.id', '');
                const bColumnId = get(b, 'columnDef.id', '');
                const aFilterOrder = get(filtersOrderMap, [entityName, aColumnId]);
                const bFilterOrder = get(filtersOrderMap, [entityName, bColumnId]);
                const aOrder = aFilterOrder ?? get(a, 'columnDef.meta.filter.order', tableColumns.indexOf(a));
                const bOrder = bFilterOrder ?? get(b, 'columnDef.meta.filter.order', tableColumns.indexOf(b));

                return aOrder - bOrder;
              })
              .map((column) => {
                const { filter = {} } = column.columnDef.meta ?? {};
                const { className, label, type = 'dropdown' } = filter;

                const selectCommonProps = {
                  labelText: label ?? (column.columnDef.header as string),
                  name: column.columnDef.id,
                  placeholder: (filter as DropdownFilter).placeholder,
                  options:
                    (column.columnDef.meta?.filter as DropdownFilter)?.options ??
                    (column.columnDef.meta?.filter as DropdownFilter)?.optionsMapping?.map((item) => ({
                      name: item.label,
                      value: item.value,
                    })) ??
                    getColumnFilterOptions(table, column.id),
                };

                return (
                  <Show key={column.columnDef.id} when={column.getCanFilter()}>
                    <Show when={type === 'checkbox'}>
                      <div>
                        <Toggle
                          value={Boolean(column.getFilterValue())}
                          className={className ?? 'mt-9 mr-6 mb-0 ml-2 xl:ml-4'}
                          labelText={label ?? (column.columnDef.header as string)}
                          onClick={() => column.setFilterValue(!column.getFilterValue())}
                        />
                      </div>
                    </Show>
                    <Show when={type === 'dropdown'}>
                      <div
                        className="mt-3"
                        key={column.columnDef.id}
                        data-testid={label ?? (column.columnDef.header as string)}
                      >
                        <ShowFirstMatching>
                          <Show when={filter.isSingleSelection}>
                            <InputSelect
                              {...selectCommonProps}
                              className={cn(selectClasses, 'w-60', className)}
                              isClearable
                              inputClassName="bg-white"
                              onChange={(value) => column.setFilterValue(value)}
                              value={column.getFilterValue() as string}
                              dataTestId={`select:${column.columnDef.header}`}
                            />
                          </Show>
                          <Show when={true}>
                            <InputMultiSelect
                              {...selectCommonProps}
                              className={cn(selectClasses, 'max-w-60', className)}
                              dropdownWrapperClassName="bg-white"
                              {...COMPACT_MULTI_SELECT_STYLE_PROPS}
                              onChange={(selectedItems) => column.setFilterValue(selectedItems)}
                              onSelect={() => {}}
                              value={(column.getFilterValue() as string[]) ?? []}
                              dataTestId={`multiselect:${column.columnDef.header}`}
                            />
                          </Show>
                        </ShowFirstMatching>
                      </div>
                    </Show>
                  </Show>
                );
              })}
            <Show when={hasActiveFilters}>
              <div className="mt-10 ml-auto">
                <Button
                  onClick={onClearAllFilters}
                  kind="secondary"
                  data-testid="clear-all-filters"
                  className="text-info-700 h-[38px]"
                >
                  <IconMdi path={mdiClose} size={0.7} />
                  Clear filters
                </Button>
              </div>
            </Show>
          </div>
        </div>
      </div>
      {onHide && <HideFiltersTab onHide={onHide} />}
    </>
  );
};
